<template>
  <ul
    class="time-counter"
    :class="{ 'time-counter--expired': isExpired, 'time-counter--stopped': isClientEndTimer && isStopped }">
    <li
      v-for="(time, title) in displayTime"
      :key="title"
      class="time-counter__item">
      <ol class="time-counter__item-numbers">
        <li
          v-for="(number, index) in (time.toString().padStart(2, '0'))"
          :key="`${title}-${index}`"
          class="time-counter__item-number"
          :class="{ 'time-counter__item-number--widget': isWidgetEndTime }">
          {{ number }}
        </li>
      </ol>

      <p
        v-if="!continueAfterExpiration"
        class="time-counter__item-text">
        {{ title }}
      </p>

      <p
        v-else-if="title === 'hours'"
        class="time-counter__item-text time-counter__item-text--no-words">
        :
      </p>
    </li>
  </ul>
</template>

<script>
export default {
  name: 'TimeCounter',

  props: {
    endTime: {
      type: String,
      required: true,
    },

    countdowns: {
      type: Array,
      default: () => (['days', 'hours', 'minutes', 'seconds']),
    },

    continueAfterExpiration: {
      type: Boolean,
      default: false,
    },

    useUtc: {
      type: Boolean,
      default: true,
    },

    showTotalHours: {
      type: Boolean,
      default: false,
    },

    isWidgetEndTime: {
      type: Boolean,
      default: false,
    },

    isClientEndTimer: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      timer: null,
      currentTime: this.getCurrentTime(),
      isExpired: false,
      displayTime: {},
      isStopped: false,
    };
  },

  computed: {
    endTimeMS() {
      return Date.parse(this.endTime);
    },
  },

  watch: {
    currentTime: {
      immediate: true,
      handler() {
        this.updateDisplayTime();
      },
    },
  },

  mounted() {
    this.startTimer();
  },

  beforeDestroy() {
    this.stopTimer();
  },

  methods: {
    getCurrentTime() {
      return this.useUtc ? Date.now() : new Date().getTime();
    },

    updateDisplayTime() {
      let total = this.endTimeMS - this.currentTime;

      if (this.isClientEndTimer && total <= 0) {
        total = 0;
        this.isStopped = true;
      } else if (this.continueAfterExpiration && total < 0) {
        total = Math.abs(total);
        this.isExpired = true;
        this.$emit('timerExpired');
      } else {
        this.isExpired = false;
        this.isStopped = false;
      }

      const timesLeft = {
        days: Math.floor(total / (1000 * 60 * 60 * 24)),
        hours: this.showTotalHours
          ? Math.floor(total / (1000 * 60 * 60))
          : Math.floor((total / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((total / 1000 / 60) % 60),
        seconds: Math.floor((total / 1000) % 60),
      };

      this.displayTime = this.countdowns.reduce((times, countdown) => {
        times[countdown] = timesLeft[countdown];
        return times;
      }, {});

      if (this.endTimeMS - this.currentTime <= 0 && !this.continueAfterExpiration && !this.isClientEndTimer) {
        this.$emit('timerStop');
      }
    },

    startTimer() {
      this.updateDisplayTime();
      this.timer = setInterval(() => {
        this.currentTime = this.getCurrentTime();
      }, 1000);
    },

    stopTimer() {
      clearInterval(this.timer);
    },
  },
};
</script>

<style lang="scss" scoped>
.time-counter {
  //--timer-number-font-size: 15px;
  //--timer-text-font-size: 13px;
  //--timer-list-gap: 12px;
  //--timer-item-gap: 4px;
  //--timer-item-flow: [column, row];

  display: grid;
  grid-auto-flow: column;
  justify-content: flex-start;
  grid-gap: var(--timer-list-gap, 12px);
}

.time-counter__item {
  display: grid;
  grid-auto-flow: var(--timer-item-flow, column);
  grid-gap: var(--timer-item-gap, 4px);
}

.time-counter__item-numbers {
  display: grid;
  grid-auto-flow: column;
  grid-gap: var(--timer-item-gap, 4px);
}

.time-counter__item-number {
  width: 1.8em;
  height: 2.1em;
  border-radius: 8px;
  font-size: var(--timer-number-font-size, 15px);
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  background-image: linear-gradient(#4E4BD9 0%, #4E4BD9 50%, $color--basic-blue 51%, $color--basic-blue 100%);
}

.time-counter--expired .time-counter__item-number {
  background-image: linear-gradient(#7A0004 0%, #7A0004 50%, #AA0000 51%, #AA0000 100%);
}

.time-counter--stopped .time-counter__item-number {
  background-image: linear-gradient($color--basic-slategray 0%, $color--basic-slategray 50%,
          $color--basic-dark-gray  51%, $color--basic-dark-gray 100%);
}

.time-counter__item-text {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--timer-text-font-size, 13px);
  font-weight: 500;
}

.time-counter__item-text--no-words {
  margin-left: 7px;
}

@media screen and #{$screen-less-than-640} {
  .time-counter__item-number {
    width: 1.3em;
    height: 1.6em;
  }

  .time-counter__item-number--widget {
    width: 1.8em;
    height: 2.1em;
  }
}
</style>
